import cn from 'clsx'
import { useState } from 'react'

import { Watchlist, WATCHLIST_NAMES, WatchlistItem } from '@App/redux/slices/watchlist/slice'
import { DollarIcon } from '@App/static/icons/AccountMenuIcon'
import { Flag } from '@App/static/icons/Flags'
import {
  AgroCultureIcon,
  AnvilIcon,
  ChartRiseDownIcon,
  ChartRiseUpIcon,
  CryptoIcon,
  EnergyIcon,
  FavouritesStarIcon,
  GoldIcon,
  HeartOutlineIcon,
  LeafsFromEarchIcon,
  OilIcon,
  PlanetIcon,
  PortfolioIcon,
  RocketIcon,
  VolatileChartIcon,
} from '@App/static/icons/WalletPageIcons'

import s from './styles.module.scss'
import { CityBuildingsIcon, MoneyExchangeIcon } from '@App/static/icons/FooterIcons'
import { ArrowDown } from '@Static/icons/DashboardIcons'

const getWatchlistIcon = (
  id: string,
  className?: string,
  fillClassName?: string,
  pathFillClassName?: string,
  pathStrokeClassName?: string,
) => {
  const WATCHLIST_ICONS: Record<string, React.ReactNode> = {
    portfolio: <PortfolioIcon className={cn(s.icon, className)} />,
    favourites: <FavouritesStarIcon className={cn(s.icon, s.pathStroke, className, pathStrokeClassName)} />,
    crypto: <CryptoIcon className={cn(s.icon, s.fill, className, fillClassName)} />,
    etf: <RocketIcon className={cn(s.icon, s.fill, className, fillClassName)} />,
    most_traded: <HeartOutlineIcon className={cn(s.icon, s.pathStroke, className, pathStrokeClassName)} />,
    indexes: <PlanetIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    top_risers: <ChartRiseUpIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    top_fallers: <ChartRiseDownIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    most_volatile: <VolatileChartIcon className={cn(s.icon, s.pathStroke, className, pathStrokeClassName)} />,
    oil_markets: <OilIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    base_metals: <AnvilIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    energies: <EnergyIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    precious_metals: <GoldIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    soft_commodities: <LeafsFromEarchIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    commodity_futures: <AgroCultureIcon className={cn(s.icon, s.pathStroke, className, pathStrokeClassName)} />,
    commods: <GoldIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
    fiat: <MoneyExchangeIcon className={cn(s.icon, s.pathStroke, className, pathStrokeClassName)} />,
    stocks: <CityBuildingsIcon className={cn(s.icon, s.pathFill, className, pathFillClassName)} />,
  }
  return WATCHLIST_ICONS[id]
}

export const WatchlistIcon = ({
  item,
  className,
  fillClassName,
  pathFillClassName,
  pathStrokeClassName,
}: {
  item: {
    key: string
    icon?: string | React.ReactNode
  }
  className?: string
  fillClassName?: string
  pathFillClassName?: string
  pathStrokeClassName?: string
}) => {
  if (typeof item.icon === 'string') {
    if (item.icon.length === 2) {
      return <Flag code={item.icon} className={s.flagIcon} />
    }

    const iconByKey = getWatchlistIcon(item.icon, className, fillClassName, pathFillClassName, pathStrokeClassName)
    if (iconByKey) {
      return <>{iconByKey}</>
    }
    return <span className={s.textIcon}>{item.icon}</span>
  }
  const icon =
    getWatchlistIcon(item.key, className, fillClassName, pathFillClassName, pathStrokeClassName) ?? item.icon ?? null
  return <>{icon}</>
}

export const WatchlistItemRow = ({
  item,
  isActive,
  onClick,
  actions,
  className,
  hasSubfilters,
  isSubfilterOpen,
  onToggleSubfilters,
  subfilters,
  selectedTab,
  onSubfilterClick,
}: {
  item: { key: string; icon?: string | React.ReactNode; name: string }
  isActive?: boolean
  onClick?: () => void
  actions?: React.ReactNode
  className?: string
  hasSubfilters?: boolean
  isSubfilterOpen?: boolean
  onToggleSubfilters?: () => void
  subfilters?: WatchlistItem[]
  selectedTab?: string | null
  onSubfilterClick?: (subfilter: WatchlistItem) => void
}) => {
  const handleClick = (e: React.MouseEvent) => {
    if (hasSubfilters && onToggleSubfilters) {
      e.stopPropagation()
      onToggleSubfilters()
    } else if (onClick) {
      onClick()
    }
  }

  return (
    <div className={cn(s.tradeCategory, isSubfilterOpen && s.active)}>
      <div className={cn(s.item, isActive && !hasSubfilters && s.active, className)} onClick={handleClick}>
        <div className={s.left}>
          <WatchlistIcon item={item} />
          {item.name || WATCHLIST_NAMES[item.key] || item.key}
        </div>
        {hasSubfilters && (
          <div className={cn(s.arrow, isSubfilterOpen ? s.open : s.close)}>
            <ArrowDown />
          </div>
        )}
        {actions}
      </div>
      {hasSubfilters && isSubfilterOpen && subfilters && (
        <div className={s.subfilters}>
          {subfilters.map((subfilter) => {
            const isSubfilterActive = subfilter.value === selectedTab
            return (
              <div
                key={subfilter.key}
                className={cn(s.subfilterItem, isSubfilterActive && s.active)}
                onClick={(e) => {
                  e.stopPropagation()
                  onSubfilterClick?.(subfilter)
                }}
              >
                <div className={s.left}>
                  {/*<WatchlistIcon item={subfilter} />*/}
                  {subfilter.name}
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export const WatchlistSingle = ({
  watchlist,
  isActive,
  onClick,
}: {
  watchlist: Watchlist
  isActive: boolean
  onClick?: () => void
}) => (
  <WatchListItemsContainer>
    <WatchlistItemRow
      item={{ key: watchlist.key, icon: watchlist.icon, name: watchlist.name }}
      isActive={isActive}
      onClick={onClick}
    />
  </WatchListItemsContainer>
)

export const WatchlistGroup = ({
  watchlist,
  selectedTab,
  onItemClick,
}: {
  watchlist: Watchlist
  selectedTab: string | null
  onItemClick?: (item?: WatchlistItem) => void
}) => {
  const [openSubfilters, setOpenSubfilters] = useState<Set<string>>(new Set())

  const toggleSubfilters = (itemKey: string) => {
    setOpenSubfilters((prev) => {
      const next = new Set(prev)
      if (next.has(itemKey)) {
        next.delete(itemKey)
      } else {
        next.add(itemKey)
      }
      return next
    })
  }

  const isItemActive = (item: WatchlistItem) => {
    if (item.isSearchWord) {
      return `${watchlist.key}-${item.value}` === selectedTab
    }

    if (item.value === selectedTab) return true
    if (Array.isArray(item.subfilters)) {
      return item.subfilters.some((subfilter) => subfilter.value === selectedTab)
    }
    return false
  }

  const handleSubfilterClick = (subfilter: WatchlistItem) => {
    if (subfilter.value) {
      onItemClick?.(subfilter)
    }
  }

  return (
    <>
      <span className={cn(s.name, watchlist.name && s.padding)}>{watchlist.name}</span>
      {watchlist.subtitle && (
        <span onClick={() => onItemClick?.()} className={cn(s.subtitle, watchlist.key === selectedTab && s.active)}>
          {watchlist.subtitle}
        </span>
      )}
      <WatchListItemsContainer>
        {watchlist.items.map((item) => {
          const hasSubfilters = Array.isArray(item.subfilters) && item.subfilters.length > 0
          const isSubfilterOpen = openSubfilters.has(item.key)

          return (
            <WatchlistItemRow
              key={item.key}
              item={item}
              isActive={isItemActive(item)}
              onClick={() => !hasSubfilters && onItemClick?.(item)}
              hasSubfilters={hasSubfilters}
              isSubfilterOpen={isSubfilterOpen}
              onToggleSubfilters={() => toggleSubfilters(item.key)}
              subfilters={hasSubfilters ? item.subfilters : undefined}
              selectedTab={selectedTab}
              onSubfilterClick={handleSubfilterClick}
            />
          )
        })}
      </WatchListItemsContainer>
      <span className={s.divider} />
    </>
  )
}
export const WatchListItemsContainer = ({ children }: { children?: React.ReactNode }) => (
  <ul className={s.items}>{children}</ul>
)
